import React, { useState, useEffect } from 'react';
import { Table, Form, Alert, Spinner, Badge } from 'react-bootstrap';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const PermissionsMatrix = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [matriz, setMatriz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(null);
  const [categoriaActiva, setCategoriaActiva] = useState('todas');

  // =====================================================
  // CARGAR MATRIZ
  // =====================================================
  useEffect(() => {
    cargarMatriz();
  }, []);

  const cargarMatriz = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/matriz-permisos`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMatriz(data.data);
      } else {
        setError(data.error || 'Error al cargar matriz');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CAMBIAR PERMISO
  // =====================================================
  const handleCambiarPermiso = async (rolId, rolCodigo, permisoCodigo, valorActual) => {
    if (rolCodigo === 'superadmin') {
      setError('No se pueden modificar los permisos del rol superadmin');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setGuardando({ rolId, permiso: permisoCodigo });
      setError(null);

      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/roles/${rolId}/permisos/${permisoCodigo}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify({ valor: !valorActual })
      });

      const data = await response.json();

      if (data.success) {
        setMatriz(prev => ({
          ...prev,
          valores: {
            ...prev.valores,
            [rolCodigo]: {
              ...prev.valores[rolCodigo],
              [permisoCodigo]: !valorActual
            }
          }
        }));
        
        onSuccess?.('Permiso actualizado');
      } else {
        setError(data.error || 'Error al actualizar permiso');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setGuardando(null);
    }
  };

  // =====================================================
  // OBTENER CATEGORÍAS ÚNICAS
  // =====================================================
  const obtenerCategorias = () => {
    if (!matriz?.permisos) return [];
    const categorias = [...new Set(matriz.permisos.map(p => p.categoria))];
    return categorias;
  };

  // =====================================================
  // FILTRAR PERMISOS POR CATEGORÍA
  // =====================================================
  const permisosFiltrados = () => {
    if (!matriz?.permisos) return [];
    if (categoriaActiva === 'todas') return matriz.permisos;
    return matriz.permisos.filter(p => p.categoria === categoriaActiva);
  };

  // =====================================================
  // RENDER - LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Cargando matriz de permisos...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-shield-check text-primary me-2"></i>
          Matriz de Permisos
        </h5>
        <small className="text-muted">Activa o desactiva permisos para cada rol</small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Info */}
      <Alert variant="info" className="mb-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Información:</strong> Usa los switches para activar o desactivar permisos. 
        Los cambios se guardan automáticamente. El rol <strong>superadmin</strong> no puede ser modificado.
      </Alert>

      {/* Filtro de categorías */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        <Badge 
          bg={categoriaActiva === 'todas' ? 'primary' : 'secondary'}
          style={{ cursor: 'pointer' }}
          onClick={() => setCategoriaActiva('todas')}
        >
          Todas
        </Badge>
        {obtenerCategorias().map(cat => (
          <Badge 
            key={cat}
            bg={categoriaActiva === cat ? 'primary' : 'secondary'}
            style={{ cursor: 'pointer' }}
            onClick={() => setCategoriaActiva(cat)}
          >
            {cat}
          </Badge>
        ))}
      </div>

      {/* Tabla de Matriz */}
      {matriz && (
        <div className="table-responsive">
          <Table bordered hover size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: '200px' }}>Permiso</th>
                {matriz.roles.map(rol => (
                  <th 
                    key={rol._id}
                    className="text-center"
                    style={{ minWidth: '100px' }}
                  >
                    <div className="d-flex align-items-center justify-content-center gap-1">
                      <div
                        className="rounded-circle"
                        style={{ 
                          width: '8px', 
                          height: '8px', 
                          backgroundColor: rol.color || '#6b7280'
                        }}
                      />
                      <span>{rol.nombre}</span>
                      {rol.esInmutable && (
                        <i className="bi bi-lock-fill text-warning" title="Protegido"></i>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permisosFiltrados().map((permiso) => (
                <tr key={permiso.codigo}>
                  <td>
                    <strong className="d-block">{permiso.nombre}</strong>
                    <small className="text-muted">{permiso.descripcion}</small>
                  </td>
                  {matriz.roles.map(rol => {
                    const valor = matriz.valores[rol.codigo]?.[permiso.codigo] || false;
                    const estaGuardando = guardando?.rolId === rol._id && guardando?.permiso === permiso.codigo;
                    const esSuperadmin = rol.codigo === 'superadmin';
                    
                    return (
                      <td 
                        key={`${rol._id}-${permiso.codigo}`}
                        className="text-center align-middle"
                      >
                        {estaGuardando ? (
                          <Spinner size="sm" animation="border" variant="primary" />
                        ) : (
                          <Form.Check
                            type="switch"
                            checked={valor}
                            disabled={esSuperadmin}
                            onChange={() => handleCambiarPermiso(rol._id, rol.codigo, permiso.codigo, valor)}
                            title={esSuperadmin ? 'No se puede modificar superadmin' : (valor ? 'Desactivar' : 'Activar')}
                            className="d-inline-block"
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Leyenda */}
      <div className="mt-3 d-flex gap-4 text-muted small">
        <span>
          <Form.Check type="switch" checked={true} disabled className="d-inline-block me-1" />
          Permiso activo
        </span>
        <span>
          <Form.Check type="switch" checked={false} disabled className="d-inline-block me-1" />
          Permiso inactivo
        </span>
        <span>
          <i className="bi bi-lock-fill text-warning me-1"></i>
          Rol protegido
        </span>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
